import { Highlight, themes } from 'prism-react-renderer'
import { useTheme } from 'next-themes'
import { isSystemDark } from '@/components/common/LightSwitch'
import { className } from '@/components/common/styles'
import { useState } from 'react'

enum WrapType {
  NO_WRAP = 'No wrap',
  WRAP_WORDS = 'Wrap words',
  WRAP_ALL = 'Wrap all',
}

const useCodeTheme = () => {
  const { theme } = useTheme()
  let codeTheme = theme === 'light' ? themes.vsLight : themes.vsDark
  if (theme === 'system') {
    if (isSystemDark(theme)) {
      codeTheme = themes.vsDark
    } else {
      codeTheme = themes.vsLight
    }
  }
  return codeTheme
}

const Wrapper = ({
  children,
  wrap,
}: {
  children: React.ReactNode
  wrap: WrapType
}) => {
  let wrapper
  // @@TODO: When to use whitespace-pre? Together with overflow-x-auto?, or add an additional wrap option?
  // @@TODO: <code contentEditable /> has nice properties, but raises a React run-time warning.
  switch (wrap) {
    case WrapType.NO_WRAP:
      wrapper = <code className="text-nowrap overflow-x-auto">{children}</code>
      break
    case WrapType.WRAP_WORDS:
      wrapper = <code className="overflow-wrap break-keep">{children}</code>
      break
    case WrapType.WRAP_ALL:
      wrapper = <code className="break-all">{children}</code>
      break
    default:
      // @@TODO: Throw error. Not all cases of switch(wrap) are handled.
      break
  }
  return wrapper
}

const Highlighter = ({ code, wrap }: { code: string; wrap: WrapType }) => {
  const codeTheme = useCodeTheme()

  return (
    <Highlight theme={codeTheme} code={code} language="javascript">
      {({ className, style, tokens, getLineProps, getTokenProps }) => (
        // NOTE: All code should be formatted as ltr (fixed).
        <pre
          dir="ltr"
          className={`rounded-md`}
          style={style}
          data-testid="fixed" // @@TODO: Make data-testid a parameter.
        >
          <div className="bg-stone-200 dark:bg-stone-800">
            <Wrapper wrap={wrap}>
              {tokens.map((line, i) => (
                <div {...getLineProps({ line })} key={i}>
                  {line.map((token, key) => (
                    <span {...getTokenProps({ token })} key={key} />
                  ))}
                </div>
              ))}
            </Wrapper>
          </div>
        </pre>
      )}
    </Highlight>
  )
}

const DataWrapper = ({ text, isCode }: { text: string; isCode: boolean }) => {
  const [wrap, setWrap] = useState<WrapType>(WrapType.NO_WRAP)

  const WrapButton = () => {
    return (
      <button
        type="button"
        className={`${className} w-28 justify-center`}
        onClick={() => {
          setWrap((value) => {
            switch (value) {
              case WrapType.NO_WRAP:
                value = WrapType.WRAP_WORDS
                break
              case WrapType.WRAP_WORDS:
                value = WrapType.WRAP_ALL
                break
              case WrapType.WRAP_ALL:
                value = WrapType.NO_WRAP
                break
              default:
                // @@TODO: Throw error
                break
            }
            return value
          })
        }}
      >
        {wrap}
      </button>
    )
  }

  // NOTE: If the text has isCode===true, wrap will not be applied.
  let formattedText = <>{text}</>
  if (isCode) {
    formattedText = <Highlighter code={text} wrap={wrap} />
  }

  return (
    <div>
      <WrapButton />
      {wrap === WrapType.NO_WRAP && (
        <Panel>
          <div className="text-nowrap">1{formattedText}</div>
        </Panel>
      )}
      {wrap === WrapType.WRAP_WORDS && (
        <Panel>
          <div className="break-keep">2{formattedText}</div>
        </Panel>
      )}
      {wrap === WrapType.WRAP_ALL && (
        <Panel>
          <div className="break-all">3{formattedText}</div>
        </Panel>
      )}
    </div>
  )
}

const Panel = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="p-1 rounded-lg bg-gray-300 dark:bg-gray-700">
      {/* https://dequeuniversity.com/rules/axe/4.6/scrollable-region-focusable?application=axeAPI */}
      <div className="tabIndex={0} break-all">
        ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ
      </div>
      <div
        className={`overflow-y-auto overflow-x-scroll bg-stone-200 dark:bg-stone-800`}
      >
        {children}
      </div>
    </div>
  )
}

export default DataWrapper
